import { styled, Tooltip } from '@mui/material';
import { ElementType, MouseEvent, ReactElement, useState } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import {
  ClickableListItem,
  ClickableListItemProps,
} from '../List/ClickableListItem';

type LelyNavigationListItemProps = {
  isActive?: boolean;
} & ClickableListItemProps;

const StyledClickableListItem = styled(ClickableListItem, {
  shouldForwardProp: (prop) => prop !== 'isActive',
})<LelyNavigationListItemProps>(({ theme, isActive }) => ({
  color: theme.palette.text.light,
  padding: theme.lelySpacing.SM,
  gap: theme.lelySpacing.MD,

  backgroundColor: isActive
    ? theme.palette.background.brandSecondary
    : theme.palette.background.brandPrimary,

  '& .icon-svg': {
    width: '24px',
    height: '24px',
    color: 'white',
  },

  '&:hover': {
    backgroundColor: theme.palette.background.primaryHover,
  },
}));

export type MenuNavigationItem = {
  label: string;
  icon: ReactElement;
  testId?: string;
  /** Internal route, rendered as a router link */
  to?: string;
  /** External URL, opened in a new tab */
  link?: string;
  tooltipLabel?: string;
  isActive?: boolean;
  showChevron?: boolean;
  onClick?: () => void;
};

type Props = MenuNavigationItem & {
  isExpanded: boolean;
};

type LinkProps = Partial<Record<'to' | 'href' | 'target' | 'rel', string>> & {
  component: ElementType;
};

const LelyNavigationListItem = ({
  label,
  icon,
  testId,
  to,
  link,
  tooltipLabel,
  showChevron = false,
  isActive,
  isExpanded,
  onClick,
}: Props) => {
  // Prevents the tooltip from triggering right after onClick
  const [disableHoverListener, setDisableHoverListener] = useState(false);
  const { pathname } = useLocation();

  const showTooltip = !isExpanded && !disableHoverListener;

  const title = showTooltip ? tooltipLabel || label : '';

  const handleClick = (event: MouseEvent) => {
    // Re-navigating to the current page would drop its search params
    const isCurrentPage = to && pathname === to.split('?')[0];
    if (isCurrentPage && !event.ctrlKey && !event.metaKey) {
      event.preventDefault();
      return;
    }

    setDisableHoverListener(true);
    onClick?.();
    setTimeout(() => setDisableHoverListener(false), 300);
  };

  const linkProps: LinkProps = to
    ? { component: RouterLink, to }
    : link
    ? {
        component: 'a',
        href: link,
        target: '_blank',
        rel: 'noopener noreferrer',
      }
    : { component: 'button' };

  return (
    <Tooltip
      key={`${label}-${isExpanded}`}
      title={title}
      placement="right"
      arrow
      data-testid="lely-navigation-list-item-tooltip"
      disableHoverListener={disableHoverListener}
    >
      <StyledClickableListItem
        {...linkProps}
        key={label}
        isActive={isActive}
        aria-current={isActive ? 'page' : undefined}
        primary={label}
        primaryIcon={icon}
        showChevron={showChevron}
        data-testid={testId}
        onClick={handleClick}
      />
    </Tooltip>
  );
};

export default LelyNavigationListItem;
